import { useMemo, type ReactNode } from "react"
import { Pencil, Plus, Trash2 } from "lucide-react"
import { LoadingSpinner } from "@/components/shared"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { DataGrid, type ColumnDef } from "@/components/ui/data-grid"

export interface Column<T> {
  header: string
  className?: string
  cell: (row: T) => ReactNode
  /**
   * Optionaler Sortier-/Filterwert (Primitivwert). Ohne Angabe wird der
   * Zellinhalt verwendet, sofern er selbst ein String/eine Zahl ist.
   */
  sortValue?: (row: T) => string | number | null | undefined
}

interface MasterDataSectionProps<T extends { id: string }> {
  isLoading: boolean
  rows: T[]
  columns: Column<T>[]
  emptyText: string
  canWrite: boolean
  canDelete: boolean
  createLabel: string
  onCreate: () => void
  onEdit: (row: T) => void
  onDelete: (row: T) => void
  deletePending?: boolean
  /** Persistenz-Schlüssel für die Spalten-Sichtbarkeit, z. B. "masterdata-teams" */
  storageKey?: string
  /** Zusaetzliche Zeilen-Aktionen (z. B. Team-Besetzung) */
  rowActions?: (row: T) => ReactNode
}

/** Generisches Stammdaten-Grid mit Inline-CRUD-Aktionen (Admin-Seite). */
export function MasterDataSection<T extends { id: string }>({
  isLoading,
  rows,
  columns,
  emptyText,
  canWrite,
  canDelete,
  createLabel,
  onCreate,
  onEdit,
  onDelete,
  deletePending,
  storageKey,
  rowActions,
}: MasterDataSectionProps<T>) {
  const showActions = canWrite || canDelete || rowActions !== undefined

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const gridColumns = useMemo<ColumnDef<T>[]>(() => {
    const defs: ColumnDef<T>[] = columns.map((c, i) => ({
      id: `${i}-${c.header}`,
      header: c.header,
      // Sortier-/Filterwert: explizit via sortValue, sonst der Zellinhalt,
      // falls er bereits ein Primitivwert ist (JSX-Zellen liefern "")
      accessorFn: (row: T) => {
        if (c.sortValue) return c.sortValue(row) ?? ""
        const v = c.cell(row)
        return typeof v === "string" || typeof v === "number" ? v : ""
      },
      cell: ({ row }) => c.cell(row.original),
      meta: { headClassName: c.className, cellClassName: c.className },
    }))
    if (showActions) {
      defs.push({
        id: "aktionen",
        header: "Aktionen",
        enableSorting: false,
        enableHiding: false,
        meta: { headClassName: "w-28 text-right", cellClassName: "text-right" },
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            {rowActions?.(row.original)}
            {canWrite && (
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                aria-label="Bearbeiten"
                onClick={() => onEdit(row.original)}
              >
                <Pencil className="!size-3.5" />
              </Button>
            )}
            {canDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="size-7 text-destructive hover:text-destructive"
                aria-label="Löschen"
                disabled={deletePending}
                onClick={() => onDelete(row.original)}
              >
                <Trash2 className="!size-3.5" />
              </Button>
            )}
          </div>
        ),
      })
    }
    return defs
  }, [columns, showActions, rowActions, canWrite, canDelete, deletePending, onEdit, onDelete])

  return (
    <div className="space-y-3">
      {canWrite && (
        <div className="flex justify-end">
          <Button onClick={onCreate}>
            <Plus /> {createLabel}
          </Button>
        </div>
      )}
      <Card>
        <CardContent className="py-2">
          {isLoading ? (
            <LoadingSpinner fullPage />
          ) : (
            <DataGrid
              columns={gridColumns}
              data={rows}
              storageKey={storageKey}
              emptyMessage={emptyText}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
